import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Rocket, RefreshCcw, BellRing, Users, Loader2, Bell, Database, ShieldAlert, Play } from "lucide-react";
import {
  fetchAdminReadiness, fetchAdminWaitlistMetrics, notifyGmailWaitlist,
  fetchAdminAlerts, resolveAdminAlert,
  fetchAdminBackups, runBackupNow,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

export const READINESS = {
  page: "admin-readiness-page",
  refreshBtn: "readiness-refresh-btn",
  notifyBtn: "readiness-notify-waitlist-btn",
  checkRow: (key) => `readiness-check-${key}`,
  alertRow: (id) => `alert-row-${id}`,
  alertResolveBtn: (id) => `alert-resolve-${id}`,
  runBackupBtn: "run-backup-btn",
};

const STATUS_META = {
  ready:   { label: "Ready",   icon: CheckCircle2,    cls: "bg-[#16271F] text-white border-[#1F3D32]",
             dot:  "bg-[var(--fcc-green)]" },
  partial: { label: "Partial", icon: AlertTriangle,   cls: "bg-amber-50 text-amber-900 border-amber-200",
             dot:  "bg-amber-500" },
  blocked: { label: "Blocked", icon: XCircle,         cls: "bg-rose-50 text-rose-900 border-rose-200",
             dot:  "bg-rose-500" },
};

export default function AdminReadinessPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [backups, setBackups] = useState({ backups: [], status: null });
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, w, a, b] = await Promise.all([
        fetchAdminReadiness(),
        fetchAdminWaitlistMetrics().catch(() => null),
        fetchAdminAlerts("open").catch(() => ({ alerts: [], open_count: 0 })),
        fetchAdminBackups().catch(() => ({ backups: [], status: null })),
      ]);
      setData(r);
      setWaitlist(w);
      setAlerts(a?.alerts || []);
      setBackups(b || { backups: [], status: null });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load readiness.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (user && user.is_admin === false) {
    return <Navigate to="/app" replace />;
  }

  const handleNotify = async () => {
    setNotifying(true);
    try {
      const r = await notifyGmailWaitlist();
      toast.success(
        r?.mode === "live"
          ? `Queued ${r.queued} live launch notifications.`
          : `Queued ${r.queued} stubbed launch notifications. Real emails will fire once RESEND_API_KEY is live.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue notifications.");
    } finally {
      setNotifying(false);
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await resolveAdminAlert(alertId);
      setAlerts((rows) => rows.filter((a) => a.id !== alertId));
      toast.success("Alert resolved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve alert.");
    }
  };

  const handleRunBackup = async () => {
    setBackingUp(true);
    try {
      const r = await runBackupNow();
      if (r?.status === "success") {
        toast.success(`Backup OK (${Math.round((r.size_bytes || 0) / 1024)} KB).`);
      } else {
        toast.error(`Backup ${r?.status}: ${r?.error || "see logs"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed.");
    } finally {
      setBackingUp(false);
    }
  };

  return (
    <div data-testid={READINESS.page} className="space-y-8">
      <header className="fcc-card p-8 flex flex-wrap items-start justify-between gap-6">
        <div className="space-y-2">
          <span className="fcc-pill fcc-chip-emma"><Rocket className="w-3 h-3" /> Launch Readiness</span>
          <h1 className="font-display text-4xl sm:text-5xl text-[var(--fcc-ink)]">Production Readiness</h1>
          <p className="text-[var(--fcc-ink-soft)] max-w-2xl leading-relaxed">
            Single-pane status for every system KidHQ needs before the Gmail launch.
            Green = Ready · Yellow = Partial · Red = Blocked.
          </p>
          {data?.summary && (
            <div className="flex flex-wrap gap-2 pt-3">
              <span className="fcc-pill bg-[var(--fcc-green)]/10 text-[var(--fcc-green)]">{data.summary.ready} ready</span>
              <span className="fcc-pill bg-amber-100 text-amber-900">{data.summary.partial} partial</span>
              <span className="fcc-pill bg-rose-100 text-rose-900">{data.summary.blocked} blocked</span>
              {data.summary.launch_ready ? (
                <span className="fcc-pill bg-[var(--fcc-ink)] text-white">🚀 Launch-ready</span>
              ) : (
                <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">Not launch-ready yet</span>
              )}
            </div>
          )}
        </div>
        <button
          data-testid={READINESS.refreshBtn}
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] disabled:opacity-50"
          disabled={loading}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          Refresh
        </button>
      </header>

      {error && (
        <div className="fcc-card p-6 bg-rose-50 border-rose-200 text-rose-900">{error}</div>
      )}

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="fcc-card p-6 lg:col-span-2">
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-5">System checks</h2>
          <ul className="space-y-3">
            {(data?.checks || []).map(c => {
              const meta = STATUS_META[c.status] || STATUS_META.partial;
              const Icon = meta.icon;
              return (
                <li
                  key={c.key}
                  data-testid={READINESS.checkRow(c.key)}
                  data-status={c.status}
                  className={`flex items-start gap-4 p-4 rounded-xl border ${meta.cls}`}
                >
                  <span className={`mt-1 w-3 h-3 rounded-full shrink-0 ${meta.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 opacity-80" />
                      <p className="font-semibold">{c.label}</p>
                      <span className="text-xs uppercase tracking-wide opacity-70">{meta.label}</span>
                    </div>
                    <p className="text-sm mt-1 opacity-90">{c.detail}</p>
                  </div>
                </li>
              );
            })}
            {loading && (data?.checks || []).length === 0 && (
              <li className="text-sm text-[var(--fcc-muted)]">Loading…</li>
            )}
          </ul>
        </div>

        <aside className="space-y-6">
          <div className="fcc-card p-6">
            <h3 className="font-display text-xl text-[var(--fcc-ink)] flex items-center gap-2">
              <Users className="w-5 h-5 text-[var(--fcc-green)]" /> Gmail waitlist
            </h3>
            {waitlist ? (
              <div className="mt-4 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-[var(--fcc-muted)]">Total signups</span>
                  <span className="text-3xl font-display text-[var(--fcc-ink)]">{waitlist.total}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-[var(--fcc-muted)]">This week</span>
                  <span className="text-xl text-[var(--fcc-ink)]">{waitlist.signups_this_week}</span>
                </div>
                <div className="pt-3 border-t border-[var(--fcc-border)] space-y-1">
                  <p className="label-overline">By source</p>
                  {(waitlist.by_source || []).length === 0 ? (
                    <p className="text-sm text-[var(--fcc-muted)]">No signups yet.</p>
                  ) : (
                    waitlist.by_source.map(s => (
                      <div key={s.source} className="flex items-center justify-between text-sm">
                        <span className="text-[var(--fcc-ink-soft)]">{s.source}</span>
                        <span className="text-[var(--fcc-ink)] font-semibold">{s.count}</span>
                      </div>
                    ))
                  )}
                </div>
                <button
                  data-testid={READINESS.notifyBtn}
                  onClick={handleNotify}
                  disabled={notifying || waitlist.total === 0}
                  className="fcc-btn-primary w-full mt-4 disabled:opacity-50"
                >
                  {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
                  Notify waitlist
                </button>
              </div>
            ) : (
              <p className="text-sm text-[var(--fcc-muted)] mt-3">Loading waitlist metrics…</p>
            )}
          </div>

          {waitlist?.recent?.length > 0 && (
            <div className="fcc-card p-6">
              <h3 className="font-display text-lg text-[var(--fcc-ink)]">Recent signups</h3>
              <ul className="mt-3 space-y-2 text-sm">
                {waitlist.recent.slice(0, 8).map(r => (
                  <li key={r.id || `${r.user_id}-${r.created_at}`} className="flex items-center justify-between">
                    <span className="text-[var(--fcc-ink-soft)] truncate max-w-[180px]">{r.email}</span>
                    <span className="text-xs text-[var(--fcc-muted)]">{(r.created_at || "").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </section>

      <footer className="text-center text-xs text-[var(--fcc-muted)] pt-4">
        Powered by All IT Solutions LLC · Generated {data?.generated_at?.slice(0, 19)?.replace("T", " ") || "—"} UTC
      </footer>

      <section data-testid="readiness-alerts-section" className="grid lg:grid-cols-3 gap-6">
        <div className="fcc-card p-6 lg:col-span-2">
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-1 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-500" /> Open alerts
          </h2>
          <p className="text-sm text-[var(--fcc-muted)] mb-5">Sweeps every 5 minutes. Resolve when you&apos;ve handled it.</p>
          {alerts.length === 0 ? (
            <div data-testid="alerts-empty-state" className="text-sm text-[var(--fcc-ink-soft)] py-6 text-center bg-[var(--fcc-cream-2)] rounded-xl">
              All clear. No open alerts.
            </div>
          ) : (
            <ul className="space-y-3">
              {alerts.map((a) => {
                const sevCls = a.severity === "critical"
                  ? "bg-rose-50 border-rose-200 text-rose-900"
                  : a.severity === "high"
                    ? "bg-orange-50 border-orange-200 text-orange-900"
                    : a.severity === "warning"
                      ? "bg-amber-50 border-amber-200 text-amber-900"
                      : "bg-slate-50 border-slate-200 text-slate-900";
                return (
                  <li
                    key={a.id}
                    data-testid={READINESS.alertRow(a.id)}
                    data-severity={a.severity}
                    className={`flex items-start gap-3 p-4 rounded-xl border ${sevCls}`}
                  >
                    <Bell className="w-4 h-4 mt-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-70">
                        <span>{a.severity}</span>
                        <span>·</span>
                        <span>{a.source}</span>
                      </div>
                      <p className="font-semibold mt-0.5">{a.message}</p>
                      <p className="text-xs text-[var(--fcc-muted)] mt-1">{(a.created_at || "").slice(0, 19).replace("T", " ")} UTC</p>
                    </div>
                    <button
                      data-testid={READINESS.alertResolveBtn(a.id)}
                      onClick={() => handleResolveAlert(a.id)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-[var(--fcc-border)] hover:bg-[var(--fcc-cream-2)]"
                    >
                      Resolve
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <aside className="fcc-card p-6" data-testid="backups-card">
          <h3 className="font-display text-xl text-[var(--fcc-ink)] flex items-center gap-2">
            <Database className="w-5 h-5 text-[var(--fcc-green)]" /> Backups
          </h3>
          {backups?.status && (
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-[var(--fcc-muted)]">Storage</span>
                <span className="text-[var(--fcc-ink)] font-semibold">{backups.status.storage}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[var(--fcc-muted)]">Retention</span>
                <span className="text-[var(--fcc-ink)] font-semibold">{backups.status.retention_days} days</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[var(--fcc-muted)]">Last backup</span>
                <span className="text-[var(--fcc-ink)] font-semibold">
                  {backups.status.latest
                    ? `${backups.status.age_hours?.toFixed(1)}h ago (${backups.status.latest.status})`
                    : "never"}
                </span>
              </div>
              {backups.status.storage === "local" && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
                  Local storage is beta-only. Move to S3/R2 for production.
                </p>
              )}
            </div>
          )}
          <button
            data-testid={READINESS.runBackupBtn}
            onClick={handleRunBackup}
            disabled={backingUp}
            className="fcc-btn-primary w-full mt-4 disabled:opacity-50"
          >
            {backingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Run backup now
          </button>

          {backups?.backups?.length > 0 && (
            <div className="mt-5 border-t border-[var(--fcc-border)] pt-3">
              <p className="label-overline">Recent runs</p>
              <ul className="mt-2 space-y-1 text-xs">
                {backups.backups.slice(0, 5).map((b) => (
                  <li key={b.id} className="flex items-center justify-between">
                    <span className="text-[var(--fcc-ink-soft)]">{(b.started_at || "").slice(0, 19).replace("T", " ")}</span>
                    <span className={b.status === "success" ? "text-[var(--fcc-green)]" : "text-rose-600"}>
                      {b.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}
